import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { ShieldAlert } from "lucide-react";

interface BlockDeviceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  deviceName: string;
}

export function BlockDeviceDialog({
  isOpen,
  onClose,
  onConfirm,
  deviceName,
}: BlockDeviceDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState("");

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm(reason || "Blocked by admin");
      onClose();
      setReason("");
    } catch (err) {
      console.error("Failed to block device:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Block Device">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-amber-50 text-amber-700 rounded-lg border border-amber-100">
          <ShieldAlert size={24} className="shrink-0" />
          <p className="text-sm font-medium">
            Blocking this device will prevent it from accessing the platform.
            You can unblock it later.
          </p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-500 mb-1">Device:</p>
          <p className="font-semibold text-gray-900">{deviceName}</p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Blocking Reason
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., suspicious activity, account sharing..."
            className="w-full h-24 p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-amber-600 hover:bg-amber-700 border-amber-600 hover:border-amber-700 text-white"
          >
            {isLoading ? "Blocking..." : "Block Device"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
